import { memo, useCallback } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { Marker, MarkerEvent } from "react-map-gl"
import { ProjectExtended } from "@nova/schemas"
import { calculateTrigonmetricDistance } from "@nova/calculate-geospatial"
import { clearActivePopup, setActivePopup } from "features/overview/slice"
import { calculatePreferredViewStateFromBoundaryBox } from "features/map/module"
import { reduceAllPointsToBoundaryBox } from "features/map/zoom/module"
import { getViewState, setNewViewstate } from "features/map/GlobalViewState"
import { useClusteredGeolocatedProjects } from "./useGeolocatedProjects"
import { LeavingMarker, MarkerCluster, MarkerPoint } from "../../useClusteredMarkers"
import { useObserveElementRect } from "@nova/toolkit-pure-hooks"
import { LiveBadge } from "features/overview/search/searchResults/LiveBadge"
import classNames from "classnames"
import { selectFireById } from "features/entity-caching/fires/selectors"

const CLUSTER_COINCIDENT_THRESHOLD_METERS = 35

const ClusterMarker = memo(function ClusterMarker({
    marker,
}: {
    marker: MarkerCluster<ProjectExtended>
}) {
    const dispatch = useAppDispatch()
    const { width, height, ref } = useObserveElementRect()
    const openClusterPopup = useCallback(() => {
        dispatch(
            setActivePopup({
                type: "projectCluster",
                id: marker.id,
                placementType: "point",
                point: { lat: marker.latitude, lng: marker.longitude },
                renderedDimensions: { width, height },
                margin: { x: -width - 1, y: -height - 1 },
            })
        )
    }, [dispatch, height, marker.id, marker.latitude, marker.longitude, width])
    const handleClick = useCallback(
        (e: MarkerEvent<MouseEvent>) => {
            e.originalEvent.stopPropagation()

            const points = marker.items.map((project): [number, number] => [
                project.location!.longitude,
                project.location!.latitude,
            ])
            const boundaryBox = reduceAllPointsToBoundaryBox([
                { longitudes: points.map((p) => p[0]), latitudes: points.map((p) => p[1]) },
            ])
            const isCoincident =
                boundaryBox &&
                calculateTrigonmetricDistance(
                    { latitude: boundaryBox.minLatitude, longitude: boundaryBox.minLongitude },
                    { latitude: boundaryBox.maxLatitude, longitude: boundaryBox.maxLongitude }
                ) <= CLUSTER_COINCIDENT_THRESHOLD_METERS

            if (!boundaryBox || isCoincident) {
                openClusterPopup()
                return
            }

            const { requiresUpdate, ...newViewState } = calculatePreferredViewStateFromBoundaryBox(
                getViewState(),
                boundaryBox,
                200
            )
            if (!requiresUpdate) return

            dispatch(clearActivePopup())
            setNewViewstate(newViewState, {
                forceFly: true,
                minFlyDurationMs: 800,
                maxFlyDurationMs: 1200,
            })
        },
        [marker.items, dispatch, openClusterPopup]
    )
    const handleContextMenu = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            e.preventDefault()
            openClusterPopup()
        },
        [openClusterPopup]
    )
    return (
        <Marker
            key={marker.id}
            longitude={marker.longitude}
            latitude={marker.latitude}
            anchor="center"
            onClick={handleClick}
        >
            <div
                ref={ref}
                className="w-7 h-7 rounded-full bg-black-400 backdrop-blur-sm border-[3px] border-white-300 flex justify-center items-center"
                onContextMenu={handleContextMenu}
            >
                <small className="text-white leading-none">{marker.items.length}</small>
            </div>
        </Marker>
    )
})

const ProjectMarker = memo(function ProjectMarker({
    marker,
}: {
    marker: MarkerPoint<ProjectExtended> | LeavingMarker<ProjectExtended>
}) {
    const dispatch = useAppDispatch()
    const project = marker.item
    const reduxProject = useAppSelector(selectFireById(project.id)) // picks up pubsub events
    const isLeaving = marker.type === "leaving"
    const animationClass = isLeaving ? "animate-to-offset" : "animate-from-offset"
    const offset = marker.type === "leaving" ? marker.targetOffsetPx : marker.animationOffsetPx
    const { width, height, ref } = useObserveElementRect()

    const style = offset
        ? ({
              "--start-x": !isLeaving ? `${offset[0]}px` : undefined,
              "--start-y": !isLeaving ? `${offset[1]}px` : undefined,
              "--end-x": isLeaving ? `${offset[0]}px` : undefined,
              "--end-y": isLeaving ? `${offset[1]}px` : undefined,
          } as React.CSSProperties)
        : {}

    const handleClick = useCallback(
        (e: MarkerEvent<MouseEvent>) => {
            e.originalEvent.stopPropagation()
            dispatch(
                setActivePopup({
                    type: "project",
                    id: project.id,
                    placementType: "point",
                    point: { lat: marker.latitude, lng: marker.longitude },
                    renderedDimensions: { width, height },
                    margin: { x: -width - 1, y: -height - 1 },
                })
            )
        },
        [dispatch, project.id, marker.latitude, marker.longitude, width, height]
    )

    if (!reduxProject) return null

    return (
        <Marker
            longitude={marker.longitude}
            latitude={marker.latitude}
            anchor="center"
            onClick={handleClick}
        >
            <div ref={ref} style={style} className={offset ? animationClass : ""}>
                <div
                    className={classNames(
                        "p-1 rounded-full max-w-[160px] h-7 flex items-center gap-2 border border-white-300 bg-black-400 backdrop-blur-sm cursor-pointer",
                        {
                            "pr-2": !!reduxProject.viewers.length,
                            "px-2": !reduxProject.viewers.length,
                        }
                    )}
                >
                    {!!reduxProject.viewers.length && (
                        <div className="w-4 h-4 shrink-0 relative flex justify-center items-center">
                            <div className="bg-blue-500 opacity-20 rounded-full inset-0 absolute" />
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                        </div>
                    )}
                    <small className="shrink min-w-0 truncate">{project.name}</small>
                    {!!reduxProject.drones?.length && (
                        <LiveBadge
                            label={`${reduxProject.drones.length > 1 ? `${reduxProject.drones.length} ` : ""}Live`}
                        />
                    )}
                </div>
            </div>
        </Marker>
    )
})

export const GeolocatedProjectMarkers = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const clustered = useClusteredGeolocatedProjects({ orgId })
    return (
        <>
            {clustered.map((marker, i) => {
                if (marker.type === "cluster") {
                    return <ClusterMarker key={`cluster-${i}`} marker={marker} />
                }
                if (marker.type === "leaving") {
                    return <ProjectMarker key={`leaving-${marker.item.id}`} marker={marker} />
                }
                return <ProjectMarker key={marker.item.id} marker={marker} />
            })}
        </>
    )
}
